/**
 * editor2/modals/GenericEditDialog.tsx
 * 汎用フィールド編集ダイアログ
 * text, textarea, number, select, color, datetime-local, time, email, tel, multi をサポート
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import type { GenericEditConfig } from '../types';

interface GenericEditDialogProps {
  open: boolean;
  config: GenericEditConfig | null;
  onSave: (value: any) => void;
  onCancel: () => void;
  onValueChange?: (value: any) => void;
}

export default function GenericEditDialog({
  open,
  config,
  onSave,
  onCancel,
  onValueChange,
}: GenericEditDialogProps) {
  if (!config) return null;

  const handleChange = (newValue: any) => {
    onValueChange?.(newValue);
  };

  const handleMultiFieldChange = (field: string, fieldValue: any) => {
    const updated = { ...config.value, [field]: fieldValue };
    onValueChange?.(updated);
  };

  const renderField = () => {
    // Multi-field mode
    if (config.type === 'multi' && config.multiFields) {
      return config.multiFields.map((field) => (
        <Box key={field.field}>
          <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
            {field.label}
          </Typography>
          {field.type === 'textarea' ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              value={config.value[field.field] ?? ''}
              onChange={(e) => handleMultiFieldChange(field.field, e.target.value)}
            />
          ) : (
            <TextField
              fullWidth
              type={field.type}
              size="small"
              value={config.value[field.field] ?? ''}
              onChange={(e) => handleMultiFieldChange(field.field, e.target.value)}
            />
          )}
        </Box>
      ));
    }

    // Single field modes
    switch (config.type) {
      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={5}
            size="small"
            value={config.value ?? ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case 'select':
        return (
          <Select
            fullWidth
            size="small"
            value={config.value ?? ''}
            onChange={(e) => handleChange(e.target.value)}
          >
            <MenuItem value="">未選択</MenuItem>
            {(config.options || []).map((o: any, i: number) => (
              <MenuItem key={i} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        );

      case 'color':
        return (
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              value={config.value ?? ''}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="#000000"
            />
            <input
              type="color"
              value={
                (config.value || '').match(/^#[0-9a-fA-F]{6}$/)
                  ? (config.value as string).toUpperCase()
                  : '#ffffff'
              }
              onChange={(e) => handleChange(e.target.value)}
              style={{
                width: 40,
                height: 40,
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
          </Box>
        );

      // text, number, datetime-local, time, email, tel
      default:
        return (
          <TextField
            fullWidth
            type={config.type || 'text'}
            size="small"
            value={config.value ?? ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, py: 2 }}>{config.title}を編集</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {renderField()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onCancel} color="inherit" sx={{ textTransform: 'none' }}>
          キャンセル
        </Button>
        <Button
          onClick={() => onSave(config.value)}
          variant="contained"
          disableElevation
          sx={{ textTransform: 'none', minWidth: 80 }}
        >
          決定
        </Button>
      </DialogActions>
    </Dialog>
  );
}
